import { useEffect, useRef, useState } from "react";
import "./landing.css";
import CostStory from "./CostStory";

const screenshots = [
  {
    id: "inputs",
    title: "资料准备",
    image: "/product/screens/inputs.jpg",
    width: 1440,
    height: 1000,
    headline: "把分析所需的资料，放在一起。",
    description:
      "按模板上传合同、还款与新增融资计划，也可手工录入。预览原始表格，定位缺项，再保存并采用资料版本。",
    points: ["文件上传与表格录入", "原始资料预览与检查", "输入版本与来源保留"],
  },
  {
    id: "calculation",
    title: "计算明细",
    image: "/product/screens/calculation.jpg",
    width: 1174,
    height: 950,
    headline: "从汇总数字，追溯到合同明细。",
    description:
      "查看本金余额、自然月账面明细与合同节点流水。不同计算口径分别呈现，每次运行保留对应成果。",
    points: [
      "本金、利息与费用明细",
      "自然月与合同节点两种口径",
      "固定运行与来源追溯",
    ],
  },
  {
    id: "analysis",
    title: "分析结果",
    image: "/product/screens/analysis.jpg",
    width: 1176,
    height: 695,
    headline: "看清成本，也看清变化的原因。",
    description:
      "从结果摘要进入图表与明细，查看合同利息、成本构成和预算或历史差异。按分析项目筛选，保留核对依据。",
    points: ["图表与明细联动", "预算、同比与环比比较", "实际记录独立核对"],
  },
  {
    id: "report",
    title: "报告交付",
    image: "/product/screens/report.jpg",
    width: 1134,
    height: 810,
    headline: "让数据与结论，成为一份完整报告。",
    description:
      "将计算成果与人工编写的分析结论汇总为报告。发布后按固定版本查阅，也可导出 PDF，用于汇报与留档。",
    points: ["分析数据与人工结论汇总", "已发布报告按版本查阅", "PDF 导出"],
  },
] as const;
type ProductScreen = (typeof screenshots)[number];
const entryUrl = "/app?source=intro";

function Icon({ name }: { name: "arrow" | "expand" | "close" | "menu" }) {
  const paths = {
    arrow: "M4 12h15M13 5l7 7-7 7",
    expand: "M9 4H4v5M15 4h5v5M20 15v5h-5M4 15v5h5",
    close: "M5 5l14 14M19 5 5 19",
    menu: "M4 6h16M4 12h16M4 18h16",
  };
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={paths[name]} />
    </svg>
  );
}

function ProductImage({
  screen,
  priority = false,
}: {
  screen: ProductScreen;
  priority?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  if (failed)
    return (
      <div className="product-image-error" role="status">
        图片暂时无法加载，请刷新页面重试。
      </div>
    );
  return (
    <img
      src={screen.image}
      alt={screen.title + "实际界面：使用示例资料的" + screen.points[0]}
      width={screen.width}
      height={screen.height}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}

function Preview({
  screen,
  onOpen,
  priority = false,
}: {
  screen: ProductScreen;
  onOpen: () => void;
  priority?: boolean;
}) {
  return (
    <figure className="product-preview">
      <div className="product-preview-bar">
        <span>融资智析 · {screen.title}</span>
        <span>界面实景</span>
      </div>
      <button
        type="button"
        className="product-preview-button"
        onClick={onOpen}
        aria-label={"放大查看" + screen.title + "截图"}
      >
        <ProductImage key={screen.id} screen={screen} priority={priority} />
        <span className="product-expand">
          <Icon name="expand" />
          放大查看
        </span>
      </button>
    </figure>
  );
}

function ImageViewer({
  index,
  onChange,
  onClose,
}: {
  index: number;
  onChange: (index: number) => void;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const screen = screenshots[index];
  useEffect(() => {
    const origin =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const overflow = document.body.style.overflow;
    const dialog = dialogRef.current;
    dialog?.showModal();
    document.body.style.overflow = "hidden";
    return () => {
      dialog?.close();
      document.body.style.overflow = overflow;
      origin?.focus();
    };
  }, []);
  function move(step: number) {
    onChange((index + step + screenshots.length) % screenshots.length);
  }
  return (
    <dialog
      ref={dialogRef}
      className="product-viewer"
      aria-labelledby="product-viewer-title"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          move(-1);
        }
        if (event.key === "ArrowRight") {
          event.preventDefault();
          move(1);
        }
      }}
    >
      <div className="product-viewer-heading">
        <h2 id="product-viewer-title">{screen.title}</h2>
        <button
          type="button"
          className="product-icon-button"
          onClick={onClose}
          aria-label="关闭截图"
        >
          <Icon name="close" />
        </button>
      </div>
      <div className="product-viewer-image">
        <ProductImage key={screen.id} screen={screen} priority />
      </div>
      <div className="product-viewer-controls">
        <button type="button" onClick={() => move(-1)}>
          上一张
        </button>
        <span aria-live="polite">
          {index + 1} / {screenshots.length}
        </span>
        <button type="button" onClick={() => move(1)}>
          下一张
        </button>
        <a href={screen.image} target="_blank" rel="noreferrer">
          查看原图
        </a>
      </div>
    </dialog>
  );
}

function Header({ gallery }: { gallery: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <header className="product-header">
      <a className="product-brand" href="/" aria-label="融资智析首页">
        <span className="product-brand-symbol" aria-hidden="true">
          融
        </span>
        <span>
          <b>融资智析</b>
          <small>FINANCING INTELLIGENCE</small>
        </span>
      </a>
      <button
        className="product-menu-button product-icon-button"
        type="button"
        aria-label={open ? "收起导航" : "展开导航"}
        aria-expanded={open}
        aria-controls="product-navigation"
        onClick={() => setOpen(!open)}
      >
        <Icon name={open ? "close" : "menu"} />
      </button>
      <nav
        id="product-navigation"
        className={open ? "product-navigation is-open" : "product-navigation"}
        aria-label="产品导航"
      >
        <a href="/#positioning" onClick={() => setOpen(false)}>
          产品介绍
        </a>
        <a href="/#workflow" onClick={() => setOpen(false)}>
          使用流程
        </a>
        <a href="/#capabilities" onClick={() => setOpen(false)}>
          核心能力
        </a>
        <a href="/product/screens" aria-current={gallery ? "page" : undefined}>
          界面实景
        </a>
        <a className="product-button product-button-lime" href={entryUrl}>
          进入系统
          <Icon name="arrow" />
        </a>
      </nav>
    </header>
  );
}

function Footer() {
  return (
    <footer className="product-footer">
      <div>
        <a className="product-footer-brand" href="/">
          融资智析
        </a>
        <p>从资料出发，让每一份分析有据可循。</p>
      </div>
      <div className="product-footer-links">
        <a href="/product/screens">浏览界面</a>
        <a href={entryUrl}>
          进入系统
          <Icon name="arrow" />
        </a>
      </div>
      <p className="product-footer-note">
        产品介绍公开访问，业务资料登录后访问。
      </p>
    </footer>
  );
}

function Home({ onOpen }: { onOpen: (index: number) => void }) {
  const [stage, setStage] = useState(0);
  const selected = screenshots[stage];
  return (
    <main id="product-main" className="product-home">
      <section className="product-hero" aria-labelledby="product-title">
        <div className="product-hero-copy">
          <h1 id="product-title">
            看清成本变化，
            <br />
            <em>让资金决策有据。</em>
          </h1>
          <p>
            贯通融资成本核算、预算与历史对标、变化归因及情景测算，为资金团队提供可核验的量化依据。
          </p>
          <div className="product-actions">
            <a className="product-button product-button-lime" href={entryUrl}>
              进入融资智析
              <Icon name="arrow" />
            </a>
            <a className="product-text-link" href="#insight">
              看一项差异如何被解释
              <Icon name="arrow" />
            </a>
          </div>
          <p className="product-hero-audience">
            面向资金分析、融资运营与财务资金管理
          </p>
        </div>
        <div className="product-hero-visual">
          <div className="product-visual-intro">
            <span>从合同依据，走向管理洞察</span>
            <span>融资成本分析</span>
          </div>
          <Preview screen={screenshots[2]} onOpen={() => onOpen(2)} priority />
          <div className="product-visual-chain" aria-label="分析价值链">
            <span>核算</span>
            <Icon name="arrow" />
            <span>对标</span>
            <Icon name="arrow" />
            <span>归因</span>
            <Icon name="arrow" />
            <span>预测</span>
          </div>
          <p className="product-caption">实际界面 · 示例资料下的合同利息分析</p>
        </div>
        <div className="product-hero-principles">
          <span>统一比较口径</span>
          <span>区分事实与假设</span>
          <span>保留计算与来源</span>
          <span>支持专业判断</span>
        </div>
      </section>
      <section id="positioning" className="product-positioning">
        <div>
          <h2>
            从“费用是多少”，
            <br />
            走向“为什么、怎么办”。
          </h2>
          <p>每一期资金分析，都应回答三个层层递进的管理问题。</p>
        </div>
        <dl>
          <div>
            <dt>发生了什么</dt>
            <dd>
              <strong>建立可信的成本全貌</strong>
              <p>
                分别核算费用归属与合同付息安排，与预算、同期和上期放在可比基础上观察。
              </p>
            </dd>
          </div>
          <div>
            <dt>为什么变化</dt>
            <dd>
              <strong>识别影响成本的关键因素</strong>
              <p>
                拆解本金规模、有效利率与计息时间的影响，结合业务证据解释差异。
              </p>
            </dd>
          </div>
          <div>
            <dt>未来如何安排</dt>
            <dd>
              <strong>让方案讨论有测算依据</strong>
              <p>
                基于合同安排与显式假设预测成本，检验关键变量变化对结果的影响。
              </p>
            </dd>
          </div>
        </dl>
      </section>
      <CostStory />
      <section
        id="capabilities"
        className="product-section product-capabilities"
      >
        <div className="product-section-heading">
          <h2>让当下可解释，让未来可评估。</h2>
          <p>一套事实基础，连接本期复盘与未来测算。</p>
        </div>
        <div className="product-capability-grid">
          <article>
            <h3>本期成本，分清口径再比较</h3>
            <p>
              费用归属、合同应付与实际执行分别保留。将季度结息、跨期入账等影响与经营变化区分，为成本判断提供更清晰的依据。
            </p>
            <dl className="product-method-list">
              <div>
                <dt>自然月核算</dt>
                <dd>解释本期归属的本金、利息与费用</dd>
              </div>
              <div>
                <dt>合同节点计算</dt>
                <dd>呈现完整付息周期下的应付安排</dd>
              </div>
              <div>
                <dt>实际记录核对</dt>
                <dd>将入账、付款与应付差额单独核查</dd>
              </div>
            </dl>
          </article>
          <article>
            <h3>未来成本，在明确条件下测算</h3>
            <p>
              固定实际起点，纳入未来合同事件与业务假设。在基准之上考察单个变量的影响，让预测结果与成立条件同时呈现。
            </p>
            <dl className="product-method-list">
              <div>
                <dt>基准预测</dt>
                <dd>依据合同与方案推演未来各期结果</dd>
              </div>
              <div>
                <dt>敏感性分析</dt>
                <dd>保持其他条件一致，检验关键变量</dd>
              </div>
              <div>
                <dt>条件与依据</dt>
                <dd>保留采用的资料、规则及方案版本</dd>
              </div>
            </dl>
          </article>
        </div>
        <p className="product-capability-note">
          测算按已配置规则与所选资料范围执行，分析结论由专业人员结合业务条件判断。
        </p>
      </section>
      <section id="workflow" className="product-section">
        <div className="product-section-heading">
          <h2>从业务事实，到可复核的分析成果</h2>
          <p>把重复整理转化为有序流程，让团队把更多精力放在解释与判断上。</p>
        </div>
        <div className="product-steps" aria-label="查看各阶段界面">
          {screenshots.map((screen, index) => (
            <button
              type="button"
              key={screen.id}
              aria-pressed={stage === index}
              aria-controls="product-stage"
              onClick={() => setStage(index)}
            >
              <span>0{index + 1}</span>
              {["准备资料", "查看计算", "分析结果", "交付报告"][index]}
              <Icon name="arrow" />
            </button>
          ))}
        </div>
        <div id="product-stage" className="product-stage">
          <div className="product-stage-copy" key={selected.id}>
            <h3>{selected.headline}</h3>
            <p>{selected.description}</p>
            <ul>
              {selected.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
            <a
              className="product-text-link"
              href={"/product/screens?view=" + selected.id}
            >
              查看这一步的界面
              <Icon name="arrow" />
            </a>
          </div>
          <Preview screen={selected} onOpen={() => onOpen(stage)} />
        </div>
        <div className="product-audience">
          <h3>同一套依据，服务不同层次的判断。</h3>
          <dl>
            <div>
              <dt>资金分析人员</dt>
              <dd>形成可复核的计算底稿与变化解释</dd>
            </div>
            <div>
              <dt>融资运营人员</dt>
              <dd>结合测算结果，评估融资调整方案</dd>
            </div>
            <div>
              <dt>财务资金负责人</dt>
              <dd>理解成本变化与预测条件，作出管理决策</dd>
            </div>
          </dl>
        </div>
      </section>
      <section className="product-section product-showcase">
        <div className="product-section-heading product-heading-with-link">
          <div>
            <h2>专业分析，落在每一步工作里。</h2>
            <p>从原始资料到固定报告版本，查看实际产品界面。</p>
          </div>
          <a className="product-text-link" href="/product/screens">
            浏览全部截图
            <Icon name="arrow" />
          </a>
        </div>
        <div className="product-showcase-grid">
          {[0, 3].map((index) => (
            <div key={screenshots[index].id}>
              <Preview
                screen={screenshots[index]}
                onOpen={() => onOpen(index)}
              />
              <h3>{screenshots[index].title}</h3>
              <p>{screenshots[index].points.join(" · ")}</p>
            </div>
          ))}
        </div>
        <p className="product-caption">
          截图来自示例资料下的实际页面，展示金额仅用于说明功能。
        </p>
      </section>
      <section className="product-section product-faq">
        <h2>
          让可信度，
          <br />
          贯穿分析的始终。
        </h2>
        <div>
          <details>
            <summary>怎样让预算与历史比较有意义？</summary>
            <p>
              在同一对象、期间和口径下比较，保留原始差额及有依据的可比调整。因素贡献与业务解释分别呈现，尚未解释的部分保持可见。
            </p>
          </details>
          <details>
            <summary>怎样核查一项结果？</summary>
            <p>
              从分析结果进入计算明细，查看对应合同、期间与金额；结合采用的输入版本、规则与实际记录，核查差异及来源。
            </p>
          </details>
          <details>
            <summary>报告如何成为可复用的管理依据？</summary>
            <p>
              将分析数据与人工结论汇总为报告，发布后按固定版本查看，并支持导出
              PDF。后续资料更新不会直接改写已发布版本。
            </p>
          </details>
        </div>
      </section>
      <section className="product-closing">
        <div>
          <h2>
            把数据转化为洞察，
            <br />
            让判断拥有依据。
          </h2>
          <p>从下一期融资成本分析开始。</p>
        </div>
        <a className="product-button product-button-green" href={entryUrl}>
          进入系统
          <Icon name="arrow" />
        </a>
      </section>
    </main>
  );
}

function Gallery({ onOpen }: { onOpen: (index: number) => void }) {
  const [selected, setSelected] = useState(() => {
    const view = new URLSearchParams(window.location.search).get("view");
    return Math.max(
      0,
      screenshots.findIndex((screen) => screen.id === view),
    );
  });
  const screen = screenshots[selected];
  function select(index: number) {
    setSelected(index);
    const url = new URL(window.location.href);
    url.searchParams.set("view", screenshots[index].id);
    window.history.replaceState(null, "", url);
  }
  return (
    <main id="product-main" className="product-gallery">
      <div className="product-gallery-heading">
        <a className="product-text-link" href="/">
          返回产品介绍
        </a>
        <h1>从界面，了解融资智析</h1>
        <p>资料、计算、分析、报告，看看每一步如何衔接。</p>
      </div>
      <div className="product-gallery-layout">
        <nav className="product-gallery-nav" aria-label="截图分类">
          {screenshots.map((item, index) => (
            <button
              type="button"
              key={item.id}
              aria-pressed={selected === index}
              aria-controls="product-gallery-content"
              onClick={() => select(index)}
            >
              {item.title}
              <Icon name="arrow" />
            </button>
          ))}
        </nav>
        <div id="product-gallery-content">
          <div className="product-gallery-description">
            <h2>{screen.headline}</h2>
            <p>{screen.description}</p>
          </div>
          <Preview screen={screen} onOpen={() => onOpen(selected)} priority />
          <ul className="product-gallery-points">
            {screen.points.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
          <p className="product-caption">
            截图来自示例资料下的实际页面，展示金额仅用于说明功能。点击截图可放大查看。
          </p>
        </div>
      </div>
    </main>
  );
}

export default function Landing() {
  const gallery =
    window.location.pathname.replace(/\/$/, "") === "/product/screens";
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  useEffect(() => {
    document.title = gallery
      ? "界面实景 · 融资智析"
      : "融资智析 · 成本洞察与资金决策支持";
  }, [gallery]);
  return (
    <div className="product-site">
      <a className="product-skip-link" href="#product-main">
        跳至主要内容
      </a>
      <Header gallery={gallery} />
      {gallery ? (
        <Gallery onOpen={setViewerIndex} />
      ) : (
        <Home onOpen={setViewerIndex} />
      )}
      <Footer />
      {viewerIndex !== null && (
        <ImageViewer
          index={viewerIndex}
          onChange={setViewerIndex}
          onClose={() => setViewerIndex(null)}
        />
      )}
    </div>
  );
}
